/**
 * useSubmissionStats Hook
 *
 * Encapsulates the logic for:
 * - Calculating submission statistics
 * - Fetching generated reports data
 * - Checking if selected submissions have reports
 *
 * Follows Single Responsibility Principle - only handles statistics logic
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProject } from "@/hooks/useProject";
import type {
  SubmissionItem,
  SubmissionStats,
  GeneratedReportsData,
} from "../store/types";

interface UseSubmissionStatsOptions {
  /** Filtered submissions to calculate stats for */
  submissions: SubmissionItem[];
  /** Currently selected submission IDs */
  selectedIds: Set<number>;
}

interface UseSubmissionStatsResult {
  /** Calculated statistics */
  stats: SubmissionStats;
  /** Generated reports data (which submissions have reports) */
  reportsData: GeneratedReportsData | undefined;
  /** Whether reports data is loading */
  isLoadingReports: boolean;
  /** Check if any selected submission has a report */
  selectedHaveAnyReport: boolean;
}

/**
 * Hook for calculating submission statistics and report status
 *
 * @example
 * ```tsx
 * const { stats, reportsData, selectedHaveAnyReport } = useSubmissionStats({
 *   submissions: filteredSubmissions,
 *   selectedIds,
 * });
 *
 * // Display stats
 * <div>Total: {stats.total}</div>
 * <div>With Individual: {stats.withIndividual}</div>
 * ```
 */
export function useSubmissionStats({
  submissions,
  selectedIds,
}: UseSubmissionStatsOptions): UseSubmissionStatsResult {
  const { projectId } = useProject();

  // Get submission IDs for querying reports
  const submissionDbIds = useMemo(() => {
    return submissions.map((s) => s.db_id).filter(Boolean) as number[];
  }, [submissions]);

  // Query generated reports for current submissions
  const { data: reportsData, isLoading: isLoadingReports } = useQuery({
    queryKey: ["generated-reports-stats", submissionDbIds, projectId],
    queryFn: async (): Promise<GeneratedReportsData> => {
      if (submissionDbIds.length === 0) {
        return { individual: new Set<number>(), referidos: new Set<number>() };
      }

      // Get all completed reports for these submissions with template info
      const { data: reports, error } = await supabase
        .from("generated_reports")
        .select(
          `
          submission_id,
          template_id,
          report_templates_v2!inner (
            id,
            jotform_form_id_referral
          )
        `
        )
        .in("submission_id", submissionDbIds)
        .eq("project_id", projectId)
        .eq("status", "completed")
        .eq("is_outdated", false);

      if (error) throw error;

      // Separate by template type
      const individualSet = new Set<number>();
      const referidosSet = new Set<number>();

      for (const report of reports || []) {
        const template = report.report_templates_v2 as {
          jotform_form_id_referral: string | null;
        } | null;
        if (template?.jotform_form_id_referral) {
          // Template de referidos (tiene jotform_form_id_referral)
          referidosSet.add(report.submission_id);
        } else {
          // Template individual (no tiene jotform_form_id_referral)
          individualSet.add(report.submission_id);
        }
      }

      return { individual: individualSet, referidos: referidosSet };
    },
    enabled: submissionDbIds.length > 0 && !!projectId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  // Calculate stats
  const stats: SubmissionStats = useMemo(() => {
    const total = submissions.length;
    const individualSet = reportsData?.individual || new Set<number>();
    const referidosSet = reportsData?.referidos || new Set<number>();

    // Count submissions with individual report
    const withIndividual = submissions.filter(
      (s) => s.db_id && individualSet.has(s.db_id)
    ).length;

    // Count submissions with referidos report (only principals with at least 1 referral)
    const withReferidos = submissions.filter(
      (s) =>
        s.db_id &&
        !s.is_referral &&
        (s.referral_count || 0) >= 1 &&
        referidosSet.has(s.db_id)
    ).length;

    // Count submissions without any report
    const withoutReport = submissions.filter(
      (s) =>
        s.db_id && !individualSet.has(s.db_id) && !referidosSet.has(s.db_id)
    ).length;

    const selected = selectedIds.size;

    return { total, withIndividual, withReferidos, withoutReport, selected };
  }, [submissions, reportsData, selectedIds.size]);

  // Check if any selected submission has a report
  const selectedHaveAnyReport = useMemo(() => {
    if (selectedIds.size === 0) return false;
    const individualSet = reportsData?.individual || new Set<number>();
    const referidosSet = reportsData?.referidos || new Set<number>();

    return Array.from(selectedIds).some(
      (id) => individualSet.has(id) || referidosSet.has(id)
    );
  }, [selectedIds, reportsData]);

  return {
    stats,
    reportsData,
    isLoadingReports,
    selectedHaveAnyReport,
  };
}
